import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Clock, ArrowRight } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending", status: "open" });
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;

    const poll = async (count) => {
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        setStatus(r.data);
        if (r.data.payment_status === "paid") return;
        if (r.data.status === "expired") return;
        if (count >= 12) return;
        timerRef.current = setTimeout(() => { setAttempts(count + 1); poll(count + 1); }, 2000);
      } catch (e) {
        if (count < 5) {
          timerRef.current = setTimeout(() => poll(count + 1), 2000);
        }
      }
    };

    poll(0);
    return () => clearTimeout(timerRef.current);
  // eslint-disable-next-line
  }, [sessionId]);

  const paid = status.payment_status === "paid";
  const expired = status.status === "expired";

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6" data-testid="billing-success-page">
      <div className="bg-white border border-zinc-200 max-w-xl w-full p-10 text-center">
        <div className="mb-6 flex justify-center">
          {paid ? (
            <div className="w-16 h-16 bg-[#DCFCE7] border-2 border-[#16A34A] flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#16A34A]" />
            </div>
          ) : expired ? (
            <div className="w-16 h-16 bg-[#FEE2E2] border-2 border-[#DC2626] flex items-center justify-center">
              <XCircle className="w-8 h-8 text-[#DC2626]" />
            </div>
          ) : (
            <div className="w-16 h-16 bg-[#FEF3C7] border-2 border-[#F59E0B] flex items-center justify-center">
              <Clock className="w-8 h-8 text-[#B45309] animate-pulse" />
            </div>
          )}
        </div>

        <div className="data-label text-[#002FA7] mb-2">Subscription Checkout</div>
        <h1 className="font-heading text-3xl font-black tracking-tight mb-3">
          {paid && "Welcome aboard."}
          {expired && "Session expired."}
          {!paid && !expired && "Confirming payment..."}
        </h1>
        <p className="text-sm text-zinc-600 mb-6">
          {paid && `Your ${status.metadata?.plan_label || "WeDelvr"} subscription is active. You now have full access to the control tower.`}
          {expired && "The checkout session expired before payment was completed. No charges were made."}
          {!paid && !expired && `Checking with Stripe... attempt ${attempts + 1}/12`}
        </p>

        {paid && (
          <div className="border-t border-zinc-200 pt-6 mb-6 grid grid-cols-2 gap-px bg-zinc-200">
            <div className="bg-white p-4 text-left">
              <div className="data-label">Plan</div>
              <div className="metric text-lg mt-1 font-medium">{status.metadata?.plan_label}</div>
            </div>
            <div className="bg-white p-4 text-left">
              <div className="data-label">Amount</div>
              <div className="metric text-lg mt-1 font-medium">${(status.amount_total / 100).toFixed(2)} {(status.currency || '').toUpperCase()}</div>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-center">
          {paid && (
            <Button data-testid="enter-tower-btn" className="rounded-none bg-[#002FA7] hover:bg-[#002480] text-white h-11 px-6" onClick={() => navigate("/dashboard")}>
              Enter Control Tower <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          {(expired || (!paid && attempts >= 12)) && (
            <Button data-testid="retry-btn" className="rounded-none bg-[#002FA7] hover:bg-[#002480] text-white h-11 px-6" onClick={() => navigate("/")}>
              Back to pricing
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
